import { ref } from 'vue';
import type { Ref } from 'vue';
import { ToastProps } from '../types';

export function useToast(): {
  toasts: Ref<ToastProps[]>;
  add: (options: ToastProps) => void;
  update: (id: string, options: Partial<ToastProps>) => void;
  remove: (id: string) => void;
} {
  const toasts = ref<ToastProps[]>([]);

  /**
   * Add a new toast to the toasts array and dispatch an `e-toast` event.
   *
   * @param {ToastProps} options - The toast options to add (must include a unique `id`).
   * @returns {void}
   */
  function add(options: ToastProps) {
    toasts.value.push(options);

    window.dispatchEvent(
      new CustomEvent('e-toast', {
        detail: options
      })
    );
  }

  /**
   * Update an existing toast by its `id`. Merges new props onto the existing toast
   * and dispatches an `e-toast-update` event. If no toast is found with the given `id`, does nothing.
   *
   * @param {ToastProps['id']} id - The unique identifier of the toast to update.
   * @param {Partial<ToastProps>} props - Partial properties to merge into the existing toast.
   * @returns {void}
   */
  function update(id: ToastProps['id'], props: Partial<ToastProps>) {
    const idx = toasts.value.findIndex((t: ToastProps) => t.id === id);
    if (idx === -1) {
      return;
    }

    const old = toasts.value[idx];
    const merged: ToastProps = { ...old, ...props };
    toasts.value[idx] = merged;

    window.dispatchEvent(
      new CustomEvent('e-toast-update', {
        detail: { id, props }
      })
    );
  }

  /**
   * Remove a toast by its `id` from the toasts array and dispatch an `e-toast-remove` event.
   * If no toast is found with the given `id`, does nothing.
   *
   * @param {ToastProps['id']} id - The unique identifier of the toast to remove.
   * @returns {void}
   */
  function remove(id: ToastProps['id']) {
    const idx = toasts.value.findIndex((t: ToastProps) => t.id === id);
    if (idx === -1) {
      return;
    }

    toasts.value.splice(idx, 1);

    window.dispatchEvent(
      new CustomEvent('e-toast-remove', {
        detail: { id }
      })
    );
  }

  return { toasts, add, update, remove };
}
